/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Hightouch Events + OneTrust", description = "Hightouch Events with OneTrust consent integration", lastUpdatedDate = new Date(1777334400000);
import {Tabs, TabList, TabPanels, Tab, TabPanel} from '@chakra-ui/react';
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Hightouch Events + OneTrust",
        description: "Hightouch Events with OneTrust consent integration",
        lastUpdatedDate: "Tue Apr 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch Events offers a pre-built integration for managing user consent with OneTrust."
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "Make sure to setup your consent categories in OneTrust before integrating with Hightouch Events."
      }), "\n", _jsxs(_components.p, {
        children: ["The integration expects the ", _jsx(_components.a, {
          href: "https://my.onetrust.com/articles/en_US/Knowledge/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226",
          children: "OneTrust Banner SDK"
        }), " to be available in order to interact with OneTrust. This library derives the group IDs that are active for the current user from the ", _jsx(_components.code, {
          children: "window.OneTrustActiveGroups"
        }), " object provided by the OneTrust SDK (", _jsx(_components.a, {
          href: "https://my.onetrust.com/articles/en_US/Knowledge/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226",
          children: "more info"
        }), ")."]
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Hightouch Events + OneTrust integration is available via ", _jsx(_components.a, {
          href: "https://npmjs.org/@ht-sdks/events-sdk-js-consent-wrapper-onetrust",
          children: "NPM"
        }), ". It can also be installed using a ", _jsx(_components.code, {
          children: "<script>"
        }), " tag."]
      }), "\n", "\n", _jsxs(Tabs, {
        children: [_jsxs(TabList, {
          children: [_jsx(Tab, {
            children: "NPM"
          }), "\n", _jsx(Tab, {
            children: "Script Tag"
          })]
        }), _jsxs(TabPanels, {
          children: [_jsxs(TabPanel, {
            children: [_jsx(_components.pre, {
              children: _jsx(_components.code, {
                className: "hljs language-sh",
                children: "npm install @ht-sdks/events-sdk-js-consent-wrapper-onetrust\n"
              })
            }), _jsx(_components.pre, {
              children: _jsxs(_components.code, {
                className: "hljs language-jsx",
                children: [_jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "import"
                }), " { ", _jsx(_components.span, {
                  className: "hljs-title class_",
                  children: "HtEventsBrowser"
                }), " } ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "from"
                }), " ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'@ht-sdks/events-sdk-js-browser'"
                }), "\n", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "import"
                }), " { withOneTrust } ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "from"
                }), " ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'@ht-sdks/events-sdk-js-consent-wrapper-onetrust'"
                }), "\n\n", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "export"
                }), " ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "const"
                }), " htevents = ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "new"
                }), " ", _jsx(_components.span, {
                  className: "hljs-title class_",
                  children: "HtEventsBrowser"
                }), "()\n\n", _jsx(_components.span, {
                  className: "hljs-title function_",
                  children: "withOneTrust"
                }), "(htevents).", _jsx(_components.span, {
                  className: "hljs-title function_",
                  children: "load"
                }), "({ ", _jsx(_components.span, {
                  className: "hljs-attr",
                  children: "writeKey"
                }), ": ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'WRITE_KEY'"
                }), " })\n"]
              })
            })]
          }), _jsx(TabPanel, {
            children: _jsx(_components.pre, {
              children: _jsxs(_components.code, {
                className: "hljs language-html",
                children: [_jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "head"
                  }), ">"]
                }), "\n  ", _jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- OneTrust -->"
                }), "\n  ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), "\n    ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "src"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"https://cdn.cookielaw.org/scripttemplates/otSDKStub.js\""
                  }), "\n    ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "type"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"text/javascript\""
                  }), "\n    ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "charset"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"UTF-8\""
                  }), "\n    ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "data-domain-script"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"YOUR-DOMAIN-SCRIPT-ID\""
                  }), "\n  >"]
                }), _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), "\n\n  ", _jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- Hightouch + OneTrust Consent Wrapper -->"
                }), "\n  ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), " ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "src"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"https://unpkg.com/@ht-sdks/events-sdk-js-consent-wrapper-onetrust@latest/dist/umd/htevents-onetrust.umd.js\""
                  }), ">"]
                }), _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), "\n\n  ", _jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- Hightouch Browser SDK snippet -->"
                }), "\n  ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), " ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "type"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"text/javascript\""
                  }), ">"]
                }), _jsxs(_components.span, {
                  className: "javascript",
                  children: ["\n    ...\n    ", _jsx(_components.span, {
                    className: "hljs-comment",
                    children: "// replace `e.load('WRITE_KEY')` with `withOneTrust(e).load('WRITE_KEY')`"
                  }), "\n    ", _jsx(_components.span, {
                    className: "hljs-title function_",
                    children: "withOneTrust"
                  }), "(e).", _jsx(_components.span, {
                    className: "hljs-title function_",
                    children: "load"
                  }), "(", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "'WRITE_KEY'"
                  }), ")\n    ...\n  "]
                }), _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), "\n", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "head"
                  }), ">"]
                }), "\n"]
              })
            })
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsxs(_components.p, {
        children: ["For a full list of features and options, see the ", _jsx(_components.a, {
          href: "https://github.com/ht-sdks/events-sdk-js-mono/tree/master/packages/consent/consent-wrapper-onetrust",
          children: "README"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
